import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import {
  CheckCircle, Circle, ArrowRight, Activity, Sparkles, GraduationCap,
  CalendarDays, BookOpen, PlayCircle, Target, ListTodo, Video, Radio, Clock, BellPlus, BellRing,
} from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import MyToolsGrid from '../../components/portal/MyToolsGrid';
import JourneyCard, { useJourney } from '../../components/portal/JourneyCard';
import UpcomingBookings from '../../components/portal/UpcomingBookings';
import InnerCircleCard from '../../components/portal/InnerCircleCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/36f0f53f-9245-4f7e-be4f-457e18932319/images/65999f9909e0849655d1d3425149620cd1ee0e1f430d38f0f944256e92240dcd.png";

const muted = { color: 'var(--p-muted)' };
const faint = { color: 'var(--p-faint)' };

const ExploreCard = ({ to, icon: Icon, title, sub, locked }) => (
  <Link to={to} className="p-card rounded-xl p-5 block hover:-translate-y-0.5" data-testid={`explore-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="w-10 h-10 rounded-lg bg-[#1D4ED8]/12 text-[#1D4ED8] flex items-center justify-center mb-3"><Icon size={19} /></div>
    <div className="font-semibold inline-flex items-center gap-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {title} {locked && <span className="text-[10px] font-bold text-[#F97316] bg-[#F97316]/10 px-1.5 py-0.5 rounded">SOON</span>}
    </div>
    <div className="text-sm mt-0.5" style={muted}>{sub}</div>
  </Link>
);

const QuickLinks = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="portal-quick-actions">
    <ExploreCard to="/portal/book" icon={CalendarDays} title="Book a Session" sub="Free jumpstart & 1-on-1" />
    <ExploreCard to="/portal/sessions" icon={PlayCircle} title="My Sessions" sub="Upcoming calls & recordings" />
    <ExploreCard to="/portal/programs" icon={GraduationCap} title="Programs" sub="Coaching offers" />
    <ExploreCard to="/portal/resources" icon={BookOpen} title="Resources" sub="Guides & downloads" />
  </div>
);

const fmtTrainWhen = (iso) => {
  if (!iso) return 'Time TBA';
  try { return new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
};

const UpNext = ({ nextSession, nextTraining, reminding, onRemind }) => {
  if (!nextSession && !nextTraining) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-4 mb-6" data-testid="portal-up-next">
      {/* Next 1-on-1 session */}
      {nextSession && (
        <div className="p-card rounded-2xl p-5 flex flex-col" data-testid="up-next-session">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-[#1D4ED8]/12 text-[#1D4ED8] flex items-center justify-center"><Video size={16} /></span>
            <span className="text-xs tracking-[0.15em] uppercase font-semibold" style={faint}>Up next · Your session</span>
          </div>
          <h3 className="font-semibold leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{nextSession.title}</h3>
          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-sm" style={muted}>
            <span className="flex items-center gap-1.5"><CalendarDays size={14} className="text-[#1D4ED8]" />{nextSession.session_date || 'TBD'}</span>
            {nextSession.session_time && <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#1D4ED8]" />{nextSession.session_time}</span>}
          </div>
          <div className="mt-auto pt-4 flex gap-2">
            {nextSession.zoom_url ? (
              <a href={nextSession.zoom_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="up-next-session-join">
                <Video size={14} /> Join via Zoom
              </a>
            ) : (
              <Link to={`/portal/sessions/${nextSession.session_id}`} className="inline-flex items-center gap-1.5 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="up-next-session-join">
                <Video size={14} /> Open session
              </Link>
            )}
            <Link to={`/portal/sessions/${nextSession.session_id}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors" style={{ borderColor: 'var(--p-border)', color: 'var(--p-muted)' }}>Details</Link>
          </div>
        </div>
      )}

      {/* Next live training */}
      {nextTraining && (
        <div className="p-card rounded-2xl p-5 flex flex-col" data-testid="up-next-training">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-[#F97316]/12 text-[#F97316] flex items-center justify-center"><Radio size={16} /></span>
            <span className="text-xs tracking-[0.15em] uppercase font-semibold" style={faint}>Up next · Live training</span>
          </div>
          <h3 className="font-semibold leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{nextTraining.title}</h3>
          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-sm" style={muted}>
            {nextTraining.status === 'live'
              ? <span className="flex items-center gap-1.5 text-[#DC2626] font-medium"><span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] animate-pulse" /> Live now</span>
              : <span className="flex items-center gap-1.5"><CalendarDays size={14} className="text-[#F97316]" />{fmtTrainWhen(nextTraining.scheduled_at)}</span>}
            {nextTraining.registered && nextTraining.status !== 'live' && <span className="flex items-center gap-1.5 text-emerald-600"><BellRing size={14} /> Reminders on</span>}
          </div>
          <div className="mt-auto pt-4 flex gap-2">
            {nextTraining.status === 'live' ? (
              <Link to={`/webinar/${nextTraining.slug}${nextTraining.join_token ? `?t=${nextTraining.join_token}` : ''}`} className="inline-flex items-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="up-next-training-join">
                <Radio size={14} /> Join live now
              </Link>
            ) : nextTraining.registered ? (
              <Link to={`/webinar/${nextTraining.slug}${nextTraining.join_token ? `?t=${nextTraining.join_token}` : ''}`} className="inline-flex items-center gap-1.5 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="up-next-training-join">
                <ArrowRight size={14} /> Open training room
              </Link>
            ) : (
              <button onClick={onRemind} disabled={reminding} className="inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60" data-testid="up-next-training-remind">
                <BellPlus size={14} /> {reminding ? 'Registering…' : 'Remind me'}
              </button>
            )}
            <Link to="/portal/training" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors" style={{ borderColor: 'var(--p-border)', color: 'var(--p-muted)' }}>All trainings</Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextSession, setNextSession] = useState(null);
  const [nextTraining, setNextTraining] = useState(null);
  const [reminding, setReminding] = useState(false);
  const journey = useJourney();

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/dashboard`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch((err) => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || '';
        if (status === 401) navigate('/login');
        if (status === 403 && !detail.toLowerCase().includes('client profile')) navigate('/');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  // "Up next" data: soonest upcoming 1-on-1 session + next live/upcoming training
  useEffect(() => {
    axios.get(`${API_URL}/api/portal/sessions`, { withCredentials: true })
      .then((res) => {
        const upcoming = (Array.isArray(res.data) ? res.data : [])
          .filter((s) => s.status !== 'completed')
          .sort((a, b) => (a.session_date || '').localeCompare(b.session_date || ''));
        setNextSession(upcoming[0] || null);
      })
      .catch(() => {});
    axios.get(`${API_URL}/api/portal/webinars`, { withCredentials: true })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setNextTraining(list.find((w) => w.status === 'live' || w.status === 'scheduled') || null);
      })
      .catch(() => {});
  }, []);

  const remindTraining = async () => {
    if (!nextTraining) return;
    setReminding(true);
    try {
      const res = await axios.post(`${API_URL}/api/portal/webinars/${nextTraining.slug}/register`, {}, { withCredentials: true });
      setNextTraining((w) => ({ ...w, registered: true, join_token: res.data.join_token }));
      toast.success("You're registered. We'll remind you before it starts.");
    } catch {
      toast.error('Could not register. Please try again.');
    } finally {
      setReminding(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout active="dashboard">
        <div className="flex items-center justify-center py-24">
          <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </PortalLayout>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'there';

  // ---------- Onboarding (no client profile yet) ----------
  if (!data) {
    return (
      <PortalLayout active="dashboard">
        <div data-testid="portal-onboarding">
          <div className="relative overflow-hidden rounded-3xl mb-8 border" style={{ borderColor: 'var(--p-border)' }} data-testid="portal-hero">
            <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(10,15,28,0.92) 0%, rgba(15,23,42,0.78) 55%, rgba(29,78,216,0.55) 100%)' }}></div>
            <div className="relative p-8 sm:p-12 max-w-2xl text-white">
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#F97316]/20 text-[#FB923C] text-xs font-bold rounded-full mb-4 border border-[#F97316]/30">
                <Sparkles size={12} /> NEW MEMBER · STEP 1
              </span>
              <h1 className="text-3xl sm:text-5xl tracking-tight font-light mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Welcome, {firstName}.
              </h1>
              <p className="text-base text-white/80 mb-6 leading-relaxed">
                Your portal activates the moment David hops on your <strong className="text-white">FREE 20-minute Business Jumpstart Session</strong>. Book one now, answer a few quick questions, and David will map out your next steps with you.
              </p>
              <Link to="/portal/book" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 rounded-lg font-medium transition-colors" data-testid="onboarding-book-cta">
                Book FREE 20-Min Jumpstart <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <InnerCircleCard />
<JourneyCard journey={journey} />
<UpcomingBookings />
          <QuickLinks />
          <MyToolsGrid />
        </div>
      </PortalLayout>
    );
  }

  // ---------- Active client ----------
  const { client, stats, recent_activities, upcoming_tasks } = data;

  return (
    <PortalLayout active="dashboard">
      <div data-testid="client-portal">
        <div className="p-card rounded-2xl p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl tracking-tight font-light" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="portal-welcome">Welcome back, {client.name?.split(' ')[0]}</h1>
              <p className="text-sm mt-1 capitalize" style={muted}>{[client.service_type, client.onboarding_status?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}</p>
            </div>
            <div className="text-right">
              <span className="text-xs" style={muted}>Getting started</span>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-40 h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--p-surface-2)' }}>
                  <div className="h-full bg-gradient-to-r from-[#1D4ED8] to-[#F97316] transition-all" style={{ width: `${journey?.progress ?? 0}%` }}></div>
                </div>
                <span className="text-sm font-bold" data-testid="portal-header-progress">{journey?.progress ?? 0}%</span>
              </div>
            </div>
          </div>
        </div>

        <UpNext
          nextSession={nextSession}
          nextTraining={nextTraining}
          reminding={reminding}
          onRemind={remindTraining}
        />

        <InnerCircleCard />
<JourneyCard journey={journey} />
<UpcomingBookings />
        <QuickLinks />
        <MyToolsGrid />


        {(stats.total_tasks + stats.total_goals + stats.upcoming_sessions) > 0 && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="portal-stats">
          {[
            { to: '/portal/tasks', icon: ListTodo, val: stats.pending_tasks, label: 'Pending Tasks', color: 'var(--p-text)' },
            { to: '/portal/tasks', icon: CheckCircle, val: stats.completed_tasks, label: 'Completed', color: '#10B981' },
            { to: '/portal/goals', icon: Target, val: stats.total_goals, label: 'Goals', color: '#1D4ED8' },
            { to: '/portal/sessions', icon: CalendarDays, val: stats.upcoming_sessions, label: 'Upcoming Sessions', color: '#F97316' },
          ].map((s, i) => (
            <Link key={i} to={s.to} className="p-card rounded-xl p-4" data-testid={`stat-${i}`}>
              <s.icon size={16} className="mb-2" style={faint} />
              <div className="text-2xl font-bold" style={{ color: s.color }}>{s.val}</div>
              <div className="text-xs" style={muted}>{s.label}</div>
            </Link>
          ))}
        </div>}

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="p-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Action Items</h2>
              <Link to="/portal/tasks" className="text-sm text-[#1D4ED8] hover:underline flex items-center gap-1">View all <ArrowRight size={14} /></Link>
            </div>
            <p className="text-xs mb-4" style={faint}>Personal next steps from your 1-on-1 and Business Jumpstart consultation sessions.</p>
            {upcoming_tasks?.length > 0 ? (
              <div className="space-y-2.5">
                {upcoming_tasks.map((task, i) => (
                  <div key={task.task_id || i} className="flex items-center gap-3 p-3 rounded-lg border" style={{ background: 'var(--p-surface-2)', borderColor: 'var(--p-border)' }} data-testid={`upcoming-task-${i}`}>
                    <Circle size={16} className="flex-shrink-0" style={faint} />
                    <div className="flex-grow min-w-0">
                      <span className="text-sm block truncate">{task.title}</span>
                      {task.due_date && <span className="text-xs" style={faint}>Due: {task.due_date}</span>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${task.priority === 'high' ? 'bg-red-500/15 text-red-500' : ''}`} style={task.priority !== 'high' ? { background: 'var(--p-hover)', ...muted } : {}}>{task.priority}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-center py-6" style={faint}>No action items yet. They'll appear here after your next session with David.</p>}
          </div>

          <div className="p-card rounded-2xl p-6">
            <h2 className="font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Activity</h2>
            {recent_activities?.length > 0 ? (
              <div className="space-y-3">
                {recent_activities.map((act, i) => (
                  <div key={act.activity_id || i} className="flex gap-3 pb-3 border-b last:border-0" style={{ borderColor: 'var(--p-border)' }} data-testid={`recent-activity-${i}`}>
                    <div className="w-8 h-8 bg-[#1D4ED8]/12 rounded-full flex items-center justify-center flex-shrink-0"><Activity size={14} className="text-[#1D4ED8]" /></div>
                    <div>
                      <span className="text-sm capitalize">{act.activity_type?.replace(/_/g, ' ')}</span>
                      <p className="text-xs" style={muted}>{act.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-center py-6" style={faint}>No recent activity</p>}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
